import React from "react";
import { Zap, Calendar, TrendingUp, ShieldCheck, Globe, CheckCircle2 } from "lucide-react";

const FeatureCard = ({ icon: Icon, title, description, className = "", highlight = false }) => (
  <div className={`
    relative overflow-hidden bg-white p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-black/5 
    shadow-sm hover:shadow-xl hover:shadow-cyan-600/5 transition-all duration-500 group 
    ${className} ${highlight ? 'ring-2 ring-cyan-600/10' : ''}
  `}>
    {/* Subtle Background Pattern for Highlights */}
    {highlight && (
      <div className="absolute top-0 right-0 -mt-4 -mr-4 w-32 h-32 bg-cyan-600/5 rounded-full blur-3xl group-hover:bg-cyan-600/10 transition-colors" />
    )}
    
    <div className={`
      w-12 h-12 sm:w-14 sm:h-14 rounded-2xl flex items-center justify-center mb-6 
      transition-all duration-500 group-hover:scale-110 
      ${highlight ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-600/20' : 'bg-slate-50 text-cyan-600'}
    `}>
      <Icon className="w-6 h-6 sm:w-7 sm:h-7" />
    </div>

    <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-3 sm:mb-4 font-['Lexend'] tracking-tight leading-tight">
      {title}
    </h3>
    
    <p className="text-slate-600 leading-relaxed text-xs sm:text-sm font-medium">
      {description}
    </p>

    {/* Feature highlight list for the big card */}
    {highlight && (
      <div className="mt-6 space-y-3 pt-6 border-t border-slate-50">
        {[
          "Under 2s response time",
          "Handles complex pricing",
          "24/7 autonomous selling"
        ].map((item, i) => (
          <div key={i} className="flex items-center gap-2 text-[10px] sm:text-xs font-bold text-slate-500">
            <CheckCircle2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-cyan-600" />
            {item}
          </div>
        ))}
      </div>
    )}
  </div>
);

const BentoGrid = () => {
  return (
    <section id="features" className="py-24 md:py-32 relative bg-white">
      <div className="absolute inset-0 bg-grid-small opacity-20" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="mb-16 text-center space-y-4">
          <p className="text-cyan-600 font-mono text-sm tracking-[0.4em] uppercase font-bold">
            Built for Performance
          </p>
          <h2 className="text-4xl md:text-6xl font-extrabold text-slate-900 font-['Lexend'] tracking-tight leading-tight">
            Engineered for <span className="text-cyan-600">Maximum ROI</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {/* Main Hero Card - Instant WhatsApp AI */}
          <FeatureCard 
            icon={Zap}
            title="Instant WhatsApp AI"
            description="Sagent responds to complex enquiries in under 2 seconds. It maintains full context of your villa rules and handles price negotiations like a human sales pro."
            className="md:col-span-8 md:row-span-2"
            highlight={true}
          />
          
          {/* Sync Anything */}
          <FeatureCard 
            icon={Calendar}
            title="Sync Anything"
            description="Direct integration with Google Calendar, Airbnb, Booking.com, MakeMyTrip, Agoda, and custom PMS systems."
            className="md:col-span-4"
          />

          {/* Secure Booking */}
          <FeatureCard 
            icon={ShieldCheck}
            title="Secure Booking"
            description="End-to-end encrypted guest data handling and secure links."
            className="md:col-span-4"
          />

          {/* ROI Focused */}
          <FeatureCard 
            icon={TrendingUp}
            title="ROI Focused"
            description="Designed to specifically increase your weekend occupancy and revenue per guest."
            className="md:col-span-6"
          />

          {/* Multi-Lingual */}
          <FeatureCard 
            icon={Globe}
            title="Multi-Lingual"
            description="Sells in 50+ languages, opening your villa to global high-net-worth tourists."
            className="md:col-span-6"
          />
        </div>
      </div>
    </section>
  );
};

export default BentoGrid;
